// V2-002 — Instant catalog filter.
//
// Hijacks clicks on `[data-instant-filter]` anchors and `change` events
// on the `[data-instant-filter-form]` form to re-fetch the catalog grid
// fragment WITHOUT a full page reload.
//
// On the wire:
//   GET /produse/<slug>/?...&_fragment=1
//   Accept: application/x-html-fragment
// → server returns _grid_fragment.html (chip-bar + grid + pagination)
// → JS swaps it into #catalog-grid-region and updates History.
//
// No-JS fallback: every link/form here works as a normal navigation
// when JS is off (the data-instant-filter attribute is just a hook).

const REGION_SEL = "#catalog-grid-region";
const RAIL_RESULT_COUNT = "[data-rail-result-count]";

function regionEl(): HTMLElement | null {
  return document.querySelector(REGION_SEL);
}

async function loadFragment(url: string): Promise<void> {
  const region = regionEl();
  if (!region) {
    location.assign(url);
    return;
  }
  region.classList.add("is-loading");

  try {
    // Append _fragment=1 so the server knows even if Accept header is
    // stripped by some proxy.
    const u = new URL(url, location.origin);
    u.searchParams.set("_fragment", "1");
    const res = await fetch(u.toString(), {
      method: "GET",
      headers: { "Accept": "application/x-html-fragment" },
      credentials: "same-origin",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const html = await res.text();
    region.innerHTML = html;

    // Update browser URL without _fragment param.
    u.searchParams.delete("_fragment");
    history.pushState({}, "", u.toString());

    // Sync mobile sticky-bottom rail counter (if visible).
    const counter = document.querySelector<HTMLElement>("[data-result-count]");
    const rail = document.querySelector<HTMLElement>(RAIL_RESULT_COUNT);
    if (counter && rail) rail.textContent = counter.textContent || "0";

    // Refire scroll-reveal on freshly-injected nodes so they animate in.
    region.querySelectorAll(".v2-reveal:not(.is-visible)").forEach((el) => {
      (el as HTMLElement).classList.add("is-visible");
    });

    // Scroll back to top of grid for context.
    region.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (err) {
    // On failure, fall back to a real navigation rather than leaving the
    // user with a stale grid + spinner.
    console.warn("[instant-filter] swap failed, navigating", err);
    location.assign(url);
  } finally {
    region.classList.remove("is-loading");
  }
}

function attach() {
  // Anchor clicks — delegated, single listener.
  document.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement)?.closest<HTMLAnchorElement>("a[data-instant-filter]");
    if (!a) return;
    // Allow ctrl/cmd/middle-click to open in new tab.
    const me = e as MouseEvent;
    if (me.ctrlKey || me.metaKey || me.shiftKey || me.button === 1) return;
    if (a.target && a.target !== "_self") return;
    e.preventDefault();
    loadFragment(a.href);
  });

  // Form change — debounced fetch on every input change.
  const form = document.querySelector<HTMLFormElement>("[data-instant-filter-form]");
  if (form) {
    let timer: number | null = null;
    const fire = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const data = new FormData(form);
        const params = new URLSearchParams();
        for (const [k, v] of data.entries()) {
          const sv = String(v).trim();
          if (sv) params.set(k, sv);
        }
        const url = location.pathname + (params.toString() ? "?" + params.toString() : "");
        loadFragment(url);
      }, 220);
    };
    form.addEventListener("change", fire);
    // Suppress submit (no-JS fallback button still works for a11y).
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      fire();
    });
  }

  // Hide the no-JS fallback submit button now that JS is alive.
  document.querySelectorAll<HTMLElement>("[data-no-js-only]").forEach((el) => {
    el.style.display = "none";
  });

  // Browser back/forward → reload fragment from URL.
  window.addEventListener("popstate", () => {
    loadFragment(location.pathname + location.search);
  });
}

if (document.readyState !== "loading") attach();
else document.addEventListener("DOMContentLoaded", attach);
